import {
  getAllMembersApi,
  getAllMembersForTraineeApi,
} from "@/app/services/members.service";
import { MemberDetailsModel } from "@/app/lib/models/MemberDetailsModel";
import { AppConstants } from "@/app/lib/util/AppConstants";
import { ResponseFormatModel } from "@/app/lib/models/ResponseFormatModel";
import CookieSessionManager from "@/app/services/session.client";
import logger from "@/lib/logger";

/** Coalesce concurrent members fetches for the same user (layout + contacts page, etc.). */
const inFlightMembersFetch = new Map<
  string,
  Promise<MemberDetailsModel[] | undefined>
>();

const fetchMembersFromApi = async (
  email: string,
  role: string,
): Promise<MemberDetailsModel[]> => {
  let response = ResponseFormatModel.invalid400Response;
  if (role === AppConstants.Roles.TRAINEE) {
    response = await getAllMembersForTraineeApi(email);
  } else if (
    role === AppConstants.Roles.GUARDIAN ||
    role === AppConstants.Roles.ADMIN ||
    role === AppConstants.Roles.CENTER_HEAD
  ) {
    response = await getAllMembersApi(email, role);
  }
  if (response && response.data && response.status == 200) {
    return response.data as MemberDetailsModel[];
  }
  if (response && response.status !== 200) {
    logger.error("Error in fetchAllMembers", { response });
    throw new Error("Error in fetchAllMembers");
  }
  return [];
};

export const fetchAllMembers = async (
  email: string,
  role: string,
  setMembersListAtom?: (value: Map<string, MemberDetailsModel>) => void,
): Promise<MemberDetailsModel[] | undefined> => {
  const cacheKey = `${email}:${role}`;
  let membersList: MemberDetailsModel[] = [];

  try {
    let fetchPromise = inFlightMembersFetch.get(cacheKey);
    if (!fetchPromise) {
      fetchPromise = fetchMembersFromApi(email, role).finally(() => {
        inFlightMembersFetch.delete(cacheKey);
      });
      inFlightMembersFetch.set(cacheKey, fetchPromise);
    }
    membersList = (await fetchPromise) ?? [];
  } catch (error) {
    logger.error("Error in fetchAllMembers", { error: String(error) });
    throw new Error(String(error));
  }

  if (setMembersListAtom) {
    const validMembers = membersList.filter(
      (user: MemberDetailsModel) => user.member_id !== undefined,
    );
    const membersMap = new Map<string, MemberDetailsModel>(
      validMembers.map((user: MemberDetailsModel) => [
        user.member_id as string,
        user,
      ]),
    );
    setMembersListAtom(membersMap);
  }
  CookieSessionManager.storeDataToBrowserSession(
    AppConstants.RouteUrls.MEMBERS,
    membersList,
  );

  return membersList;
};
